import {
  CmdCalendar,
  CmdError,
  CmdModal,
  CmdModalFooterButton,
} from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useForm, Controller } from 'react-hook-form';
import {
  DAY_TIME_ONLY_DAY,
  DAY_TIME_ONLY_NIGHT,
  DAY_TIME_WEEKEND,
  DAY_TIME_WORKWEEK,
  DEFAULT_VALUES_DAY_TIME_FORM,
  ICheckedTableItems,
  ITripsOverviewAdvancedFilters,
  ITripsOverviewTableData,
} from '../interfaces';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  hasMoreAtom,
  isActiveAdvanceFilterAtom,
  offsetAtom,
  rideBookDataAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import { convertTimeToSeconds, defineWeekdays } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IDayTimeFormInput {
  days: string[];
  timeFrom: Date;
  timeTo: Date;
  closedInterval: boolean;
}

interface IDayTimeFilter {
  handleClose: () => void;
}

export default function DayTimeFilter(props: IDayTimeFilter): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [dayTimeValue, setDayTimeValue] = useRecoilState(advancedFiltersAtom);
  const setIsActiveAdvanceFilter = useSetRecoilState(isActiveAdvanceFilterAtom);
  const [, setDayTimeFormValue] = React.useState<IDayTimeFormInput>(DEFAULT_VALUES_DAY_TIME_FORM);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const { trackEvent } = useAnalytics();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors },
    watch,
    reset,
    clearErrors,
  } = useForm({
    defaultValues: {
      ...dayTimeValue,
    },
  });

  const onSubmit = (data: ITripsOverviewAdvancedFilters) => {
    if (data.days.length === 0) {
      setError('days', {
        message: f({
          id: 'tables.tripsOverview.modal.dayTime.days.validation',
        }),
      });
      return;
    }
    trackEvent(['ride book', 'click', 'Ride book day time filter submit']);

    setDayTimeValue(data);
    setIsActiveAdvanceFilter((oldState) => ({ ...oldState, days: true, time: true }));
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });
    props.handleClose();
  };

  const weekDays = defineWeekdays();

  const handleWeekDays = (
    language: string
  ): { name: string; shortName: string; indexOfDay: string }[] => {
    const tempArray: { name: string; shortName: string; indexOfDay: string }[] = [];
    if (weekDays.weekdays && weekDays.weekdaysMin) {
      weekDays.weekdays.forEach((item, index) => {
        if (weekDays.weekdaysMin) {
          tempArray.push({
            name: item,
            shortName: weekDays.weekdaysMin[index],
            indexOfDay: String(index),
          });
        }
      });

      if (language !== 'en') {
        return tempArray.slice(1).concat(tempArray[0]);
      }

      return tempArray;
    }

    return [];
  };
  const handleResetButton = (): boolean => {
    trackEvent(['ride book', 'click', 'Ride book day time filter reset']);
    const array1 = watch('days').sort();
    const array2 = [...DEFAULT_VALUES_DAY_TIME_FORM.days].sort();

    // Compare arrays
    const arraysAreEqual = array2.every((value, index) => value === array1[index]);

    if (
      !arraysAreEqual ||
      convertTimeToSeconds(watch('timeFrom')) !==
        convertTimeToSeconds(DEFAULT_VALUES_DAY_TIME_FORM.timeFrom) ||
      convertTimeToSeconds(watch('timeTo')) !==
        convertTimeToSeconds(DEFAULT_VALUES_DAY_TIME_FORM.timeTo) ||
      watch('closedInterval') !== DEFAULT_VALUES_DAY_TIME_FORM.closedInterval
    ) {
      return true;
    }
    return false;
  };

  const handleResetForm = () => {
    reset(DEFAULT_VALUES_DAY_TIME_FORM);

    setDayTimeFormValue({
      days: [],
      timeFrom: new Date(new Date().setHours(0, 0, 0, 0)),
      timeTo: new Date(new Date().setHours(23, 59, 0, 0)),
      closedInterval: false,
    });
    handleResetButton();
  };

  const buttons: CmdModalFooterButton[] = handleResetButton()
    ? [
        {
          id: 'modalClose',
          type: 'button',
          title: 'Reset',
          className: 'e-button',
          // closeCallback: props.handleClose,
          // submitCallback: handleResetForm,
          callback: handleResetForm,
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.resetBtn.title' }),
        },
        {
          id: 'submitEditModal',
          type: 'submit',
          title: f({ id: 'table.apply' }),
          className: 'e-button e-button--gray',
          submitCallback: handleSubmit(onSubmit),
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.applyBtn.title' }),
        },
      ]
    : [
        {
          id: 'submitEditModal',
          type: 'submit',
          title: f({ id: 'table.apply' }),
          className: 'e-button e-button--gray',
          submitCallback: handleSubmit(onSubmit),
          tooltip: f({ id: 'tables.tripsOverview.modal.dayTime.applyBtn.title' }),
        },
      ];

  const handlePredefinedButtons = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const id = event.currentTarget.id;
    trackEvent(['ride book', 'click', `Ride book day time filter predefined button: ${id}`]);

    switch (id) {
      case DAY_TIME_ONLY_DAY:
        setValue('timeFrom', new Date(new Date().setHours(6, 0, 0, 0)));
        setValue('closedInterval', true);
        setDayTimeFormValue((oldState) => {
          return {
            ...oldState,
            timeFrom: new Date(new Date().setHours(6, 0, 0, 0)),
            closedInterval: true,
          };
        });
        setValue('timeTo', new Date(new Date().setHours(22, 0, 0, 0)));
        setValue('closedInterval', true);
        setDayTimeFormValue((oldState) => {
          return {
            ...oldState,
            timeTo: new Date(new Date().setHours(22, 0, 0, 0)),
            closedInterval: true,
          };
        });
        break;
      case DAY_TIME_ONLY_NIGHT:
        setValue('timeFrom', new Date(new Date().setHours(22, 0, 0, 0)));
        setValue('closedInterval', true);
        setDayTimeFormValue((oldState) => {
          return {
            ...oldState,
            timeFrom: new Date(new Date().setHours(22, 0, 0, 0)),
            closedInterval: true,
          };
        });
        setValue('timeTo', new Date(new Date().setHours(6, 0, 0, 0)));
        setValue('closedInterval', true);
        setDayTimeFormValue((oldState) => {
          return {
            ...oldState,
            timeTo: new Date(new Date().setHours(6, 0, 0, 0)),
            closedInterval: true,
          };
        });
        break;
      case DAY_TIME_WORKWEEK:
        setValue('days', ['1', '2', '3', '4', '5']);
        setDayTimeFormValue((oldState) => {
          return { ...oldState, days: ['1', '2', '3', '4', '5'] };
        });
        if (errors.days && watch('days').length > 0) {
          clearErrors('days');
        }
        break;
      case DAY_TIME_WEEKEND:
        setValue('days', ['0', '6']);
        setDayTimeFormValue((oldState) => {
          return { ...oldState, days: ['0', '6'] };
        });
        if (errors.days && watch('days').length > 0) {
          clearErrors('days');
        }
        break;

      default:
        break;
    }
  };

  return (
    <CmdModal
      id="day-time-filter-ride-book-modal"
      title={f({ id: 'tables.tripsOverview.modal.dayTime.title' })}
      footerButtons={buttons}
      // loader={true}
      // size="xl"
      width="720px"
      closeCallbackForCloseIcon={props.handleClose}
    >
      <div className="px-4 py-2" style={{ margin: '-24px 0' }}>
        <div className="form-group mb-4">
          <a
            id={DAY_TIME_ONLY_DAY}
            href="#"
            className="e-table-label mb-2 mr-1"
            data-cy="dayTime-form-onlyday"
            onClick={handlePredefinedButtons}
            title={f({ id: 'tables.tripsOverview.modal.dayTime.onlyDay.title' })}
          >
            {f({ id: 'tables.tripsOverview.modal.dayTime.onlyDay' })}
          </a>
          <a
            id={DAY_TIME_ONLY_NIGHT}
            href="#"
            className="e-table-label mb-2 mr-1"
            data-cy="dayTime-form-onlynight"
            onClick={handlePredefinedButtons}
            title={f({ id: 'tables.tripsOverview.modal.dayTime.onlyNight.title' })}
          >
            {f({ id: 'tables.tripsOverview.modal.dayTime.onlyNight' })}
          </a>

          <a
            id={DAY_TIME_WORKWEEK}
            href="#"
            className="e-table-label mb-2 mr-1"
            data-cy="dayTime-form-workweek"
            onClick={handlePredefinedButtons}
            title={f({ id: 'tables.tripsOverview.modal.dayTime.workWeek.title' })}
          >
            {f({ id: 'tables.tripsOverview.modal.dayTime.workWeek' })}
          </a>
          <a
            id={DAY_TIME_WEEKEND}
            href="#"
            className="e-table-label mb-2 mr-1"
            data-cy="dayTime-form-weekend"
            onClick={handlePredefinedButtons}
            title={f({ id: 'tables.tripsOverview.modal.dayTime.weekend.title' })}
          >
            {f({ id: 'tables.tripsOverview.modal.dayTime.weekend' })}
          </a>
        </div>
        <div className={`form-group ${errors.days ? `form-group-error` : ''}`}>
          <div className="row">
            <label
              htmlFor=""
              className="col-12 col-lg-2 form-label"
              title={f({ id: 'tables.tripsOverview.modal.dayTime.weekdays.title' })}
            >
              {errors.days && <span className="text-danger required-field-info">*</span>}
              {f({ id: 'tables.tripsOverview.modal.dayTime.weekdays' })}
            </label>
            <div className="col-12 col-lg-10">
              <div className="row">
                {handleWeekDays(weekDays.name).map((item, index: number) => (
                  <div
                    className="col-3 col-md-auto mb-1"
                    title={item.name}
                    key={`${item.name}-${index}`}
                    data-cy={`dayTime-form-checkbox-title-${index}`}
                  >
                    <div className="e-checkbox font-size-sm">
                      <label htmlFor={item.name}>
                        <input
                          name="days"
                          id={item.name}
                          data-id={item.indexOfDay}
                          data-cy={`dayTime-form-checkbox-${index}`}
                          type="checkbox"
                          checked={getValues().days.includes(String(item.indexOfDay))}
                          onChange={(event) => {
                            const indexDay = event.currentTarget.getAttribute('data-id');
                            const daysArray = getValues().days;

                            if (indexDay && daysArray.includes(indexDay)) {
                              const tempArray = daysArray.filter((day) => day !== indexDay);

                              setValue('days', tempArray);
                              setDayTimeFormValue((oldState) => {
                                return { ...oldState, days: tempArray };
                              });
                            } else if (indexDay && !daysArray.includes(indexDay)) {
                              const tempArray = [...daysArray];
                              tempArray.push(indexDay);

                              setValue('days', tempArray);
                              setDayTimeFormValue((oldState) => {
                                return { ...oldState, days: tempArray };
                              });
                            }
                            if (errors.days && watch('days').length > 0) {
                              clearErrors('days');
                            }
                          }}
                        />
                        <span>{item.shortName}</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              {errors.days && errors.days.message && <CmdError message={errors.days.message} />}
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row align-items-center">
            <label htmlFor="#" className="col-12 col-lg-2 form-label">
              {f({ id: 'tables.tripsOverview.modal.dayTime.run' })}
            </label>
            <div className="col-lg-10">
              <div className="row no-gutters align-items-center">
                <div className="col-6 col-md-3 col-lg-3 pr-1" style={{ marginLeft: '-8px' }}>
                  <Controller
                    name="timeFrom"
                    control={control}
                    render={({ field }) => (
                      <CmdCalendar
                        {...field}
                        id="timeFrom"
                        mode="time"
                        calendarTranslations={{
                          sunday: f({ id: 'date.sunday' }).slice(0, 2),
                          monday: f({ id: 'date.monday' }).slice(0, 2),
                          tuesday: f({ id: 'date.tuesday' }).slice(0, 2),
                          wednesday: f({ id: 'date.wednesday' }).slice(0, 2),
                          thursday: f({ id: 'date.thursday' }).slice(0, 2),
                          friday: f({ id: 'date.friday' }).slice(0, 2),
                          saturday: f({ id: 'date.saturday' }).slice(0, 2),
                          ok: f({ id: 'table.apply' }),
                          today: f({ id: 'datepicker.TODAY' }),
                          yesterday: f({ id: 'datepicker.YESTERDAY' }),
                          hours: f({ id: 'date.hours' }),
                          minutes: f({ id: 'date.minutes' }),
                          // seconds: f({ id: 'date.seconds' }),
                        }}
                        onChangeCallback={(value) => {
                          setValue('timeFrom', value as Date);
                          setDayTimeFormValue((oldState) => {
                            return { ...oldState, timeFrom: value as Date };
                          });
                          trackEvent(['ride book', 'click', 'Ride book day time filter time from']);
                        }}
                        noLabel
                        tooltip={f({ id: 'tables.tripsOverview.modal.dayTime.timeFrom.title' })}
                      />
                    )}
                  />
                </div>
                <div className="col-6 col-md-3 col-lg-3 pr-1" style={{ marginLeft: '-2px' }}>
                  <Controller
                    name="timeTo"
                    control={control}
                    render={({ field }) => (
                      <CmdCalendar
                        {...field}
                        id="timeTo"
                        mode="time"
                        calendarTranslations={{
                          sunday: f({ id: 'date.sunday' }).slice(0, 2),
                          monday: f({ id: 'date.monday' }).slice(0, 2),
                          tuesday: f({ id: 'date.tuesday' }).slice(0, 2),
                          wednesday: f({ id: 'date.wednesday' }).slice(0, 2),
                          thursday: f({ id: 'date.thursday' }).slice(0, 2),
                          friday: f({ id: 'date.friday' }).slice(0, 2),
                          saturday: f({ id: 'date.saturday' }).slice(0, 2),
                          ok: f({ id: 'table.apply' }),
                          today: f({ id: 'datepicker.TODAY' }),
                          yesterday: f({ id: 'datepicker.YESTERDAY' }),
                          hours: f({ id: 'date.hours' }),
                          minutes: f({ id: 'date.minutes' }),
                          // seconds: f({ id: 'date.seconds' }),
                        }}
                        noLabel
                        onChangeCallback={(value) => {
                          setValue('timeTo', value as Date);
                          setDayTimeFormValue((oldState) => {
                            return { ...oldState, timeTo: value as Date };
                          });
                          trackEvent(['ride book', 'click', 'Ride book day time filter time to']);
                        }}
                        tooltip={f({ id: 'tables.tripsOverview.modal.dayTime.timeTo.title' })}
                      />
                    )}
                  />
                </div>
                <div className="col-4 col-md-auto ml-md-2 ml-lg-8 ml-xl-6 mt-2 mt-md-0 position-relative left-1">
                  <div className="e-checkbox font-size-sm">
                    <label htmlFor="daytime-interval">
                      <input
                        name="closedInterval"
                        id="daytime-interval"
                        data-cy="dayTime-form-checkbox-title-closeInterval"
                        type="checkbox"
                        checked={getValues().closedInterval}
                        onChange={(event) => {
                          setValue('closedInterval', !getValues().closedInterval);
                          setDayTimeFormValue((oldState) => {
                            return { ...oldState, closedInterval: getValues().closedInterval };
                          });
                          trackEvent([
                            'ride book',
                            'click',
                            'Ride book day time filter closed interval',
                          ]);
                        }}
                      />
                      <span>{f({ id: 'tables.tripsOverview.modal.dayTime.closeInterval' })}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CmdModal>
  );
}
